import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Spinner } from "../components/ui/Spinner";
import { useToast } from "../components/ui/Toast";
import { Input } from "../components/ui/Input";
import { 
  AlertCircle, 
  Search, 
  Plus, 
  RefreshCw, 
  Eye,
  Edit,
  Trash2,
  Grid3X3,
  List,
  X,
  Users,
  UserCheck,
  Shield,
  Clock,
  MoreHorizontal,
  User,
  Star,
  Loader2
} from 'lucide-react';
import { useUsersApi } from '../apis/users-apis';
import { Button } from "../components/ui/Button";
import UserDetailModal from '../components/UserDetailModal';
import _ from 'lodash';
import { UserData } from '../types';

type FilterType = 'all' | 'active' | 'inactive' | 'admin';
type SortType = 'name' | 'role' | 'created' | 'login';
type ViewMode = 'grid' | 'list';

const UserManagementPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [sortType, setSortType] = useState<SortType>('name');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const { addToast } = useToast();
  const { getUsers } = useUsersApi();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const data = await getUsers();
        setUsers(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load users');
        addToast({
          title: "Error",
          description: "Failed to load users. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Filter and sort users
  const getFilteredAndSortedUsers = () => {
    let filtered = users;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.userId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    switch (filterType) {
      case 'active':
        filtered = filtered.filter(user => user.isActive);
        break;
      case 'inactive':
        filtered = filtered.filter(user => !user.isActive);
        break;
      case 'admin':
        filtered = filtered.filter(user => user.isAdmin);
        break;
      default:
        break;
    }

    // Apply sorting
    switch (sortType) {
      case 'name':
        filtered = _.sortBy(filtered, 'email');
        break;
      case 'role':
        filtered = _.sortBy(filtered, user => user.isAdmin ? 0 : 1);
        break;
      case 'created':
        filtered = _.sortBy(filtered, 'createdAt').reverse();
        break;
      case 'login':
        filtered = _.sortBy(filtered, 'lastLogin').reverse();
        break;
      default:
        break;
    }

    return filtered;
  };

  const filteredUsers = getFilteredAndSortedUsers();

  // Stats calculations
  const stats = useMemo(() => {
    const active = users.filter(user => user.isActive).length;
    const inactive = users.filter(user => !user.isActive).length;
    const admins = users.filter(user => user.isAdmin).length;
    
    return { total: users.length, active, inactive, admins };
  }, [users]);

  const handleViewUser = (userId: string) => {
    setSelectedUserId(userId);
    setIsDetailModalOpen(true);
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      setIsDeletingId(userId);
      // await deleteUser(userId); // Add your delete API call here
      setUsers(prev => prev.filter(u => u.userId !== userId));
      
      addToast({
        title: "Success",
        description: "User has been deleted.",
        variant: "default",
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      addToast({
        title: "Error",
        description: "Failed to delete user. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderUserCard = (user: UserData) => {
    return (
      <Card key={user.userId} className="group hover:shadow-xl transition-all duration-300 border-purple-200 hover:border-purple-400 hover:scale-[1.02] relative ring-2 ring-transparent hover:ring-purple-200/50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            {/* Left side - Avatar and Info */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {/* User Avatar */}
              <div className="relative flex-shrink-0">
                {user.profileImage ? (
                  <img
                    src={user.profileImage}
                    alt={user.email}
                    className="w-12 h-12 rounded-xl object-cover border-2 border-purple-200 shadow-sm"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-indigo-200 flex items-center justify-center text-purple-600 font-semibold text-lg border-2 border-purple-200 shadow-sm">
                    {user.email.charAt(0).toUpperCase()}
                  </div>
                )}
                {/* Status indicator */}
                <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
                  user.isActive ? 'bg-green-500' : 'bg-gray-400'
                }`}></div>
              </div>
              
              {/* User Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-purple-600 transition-colors truncate tracking-tight">
                    {user.email}
                  </h3>
                  {user.isAdmin && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-purple-400 to-purple-500 text-white shadow-sm flex-shrink-0">
                      <Shield className="w-3 h-3 mr-1" />
                      Admin
                    </span>
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-2">
                  ID: {user.userId}
                </p>
                
                <div className="flex items-center gap-2">
                  <Badge
                    variant={user.isActive ? "default" : "secondary"}
                    className={`${
                      user.isActive 
                        ? "bg-green-100 text-green-800 border-green-300" 
                        : "bg-gray-100 text-gray-800 border-gray-300"
                    }`}
                  >
                    {user.isActive ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 ml-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleViewUser(user.userId)}
                className="h-8 w-8 p-0 hover:bg-purple-50 hover:text-purple-600"
                title="View User"
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {/* Handle edit */}}
                className="h-8 w-8 p-0 hover:bg-violet-50 hover:text-violet-600"
                title="Edit User"
              >
                <Edit className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteUser(user.userId)}
                className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                disabled={isDeletingId === user.userId}
                title="Delete User"
              >
                {isDeletingId === user.userId ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Bottom section - Stats */}
          <div className="space-y-3">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1 font-medium">
                <Clock className="w-3 h-3 text-blue-500" />
                Created {formatDate(user.createdAt)}
              </span>
              <span className="flex items-center gap-1 font-medium">
                <UserCheck className="w-3 h-3 text-emerald-500" />
                Last login {formatDate(user.lastLogin)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderListItem = (user: UserData) => {
    return (
      <Card key={user.userId} className="mb-3 hover:shadow-lg transition-all duration-200 hover:border-blue-300 group">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              {/* User Avatar */}
              <div className="relative flex-shrink-0">
                {user.profileImage ? (
                  <img
                    src={user.profileImage}
                    alt={user.email}
                    className="w-10 h-10 rounded-lg object-cover border border-blue-200"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center text-blue-600 font-semibold">
                    {user.email.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border border-white ${
                  user.isActive ? 'bg-green-500' : 'bg-gray-400'
                }`}></div>
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <h3 className="text-lg font-semibold text-gray-900 truncate tracking-tight">
                    {user.email}
                  </h3>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={user.isActive ? "default" : "secondary"}
                      className={`${
                        user.isActive 
                          ? "bg-green-100 text-green-800 border-green-300" 
                          : "bg-gray-100 text-gray-800 border-gray-300"
                      }`}
                    >
                      {user.isActive ? "Active" : "Inactive"}
                    </Badge>
                    {user.isAdmin && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-purple-400 to-purple-500 text-white shadow-sm">
                        <Shield className="w-3 h-3 mr-1" />
                        Admin
                      </span>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  Created {formatDate(user.createdAt)} • Last login {formatDate(user.lastLogin)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2 ml-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewUser(user.userId)}
                className="hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300"
              >
                <Eye className="w-4 h-4 mr-2" />
                View
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-gray-50"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner className="w-8 h-8" />
          <p className="text-gray-600">Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-red-500" />
              <div>
                <p className="text-red-700 font-medium">Error loading users</p>
                <p className="text-red-600 text-sm">{error}</p>
              </div>
              <Button 
                onClick={() => window.location.reload()} 
                variant="outline" 
                className="ml-auto"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl font-['Poppins','SF_Pro_Display','system-ui','-apple-system','sans-serif'] antialiased">
      {/* Hero Header Section */}
      <div className="relative mb-8 overflow-hidden">
        {/* Multi-layer gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-indigo-600 to-purple-800"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/30 via-transparent to-indigo-500/30"></div>
        
        {/* Animated background elements */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-white/5 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-indigo-400/10 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-purple-400/5 rounded-full blur-3xl animate-pulse delay-500"></div>
        
        {/* Glassmorphism overlay */}
        <div className="absolute inset-0 backdrop-blur-sm bg-gradient-to-r from-white/10 to-transparent"></div>
        
        {/* Content */}
        <div className="relative z-10 px-4 sm:px-8 py-8 sm:py-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
                <div className="bg-white/20 backdrop-blur-sm p-3 rounded-2xl shadow-lg self-start">
                  <Users className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-2 drop-shadow-lg">
                    User Management
                  </h1>
                  <p className="text-purple-100 text-base sm:text-lg font-medium max-w-2xl leading-relaxed">
                    Manage user accounts, permissions, and access controls for your organization with comprehensive administrative tools
                  </p>
                </div>
              </div>
              
              {/* Live Stats */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-6">
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-white">{stats.total}</span> Total Users
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-emerald-200">{stats.active}</span> Active Users
                  </span>
                </div>
                <div className="bg-white/10 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/20">
                  <span className="text-white/90 text-xs sm:text-sm font-medium">
                    <span className="text-lg sm:text-2xl font-bold text-purple-200">{stats.admins}</span> Admins
                  </span>
                </div>
              </div>
            </div>
            
            {/* Action Button */}
            <div className="flex items-center gap-3">
              <Button
                onClick={() => {/* Handle add user */}}
                className="group relative bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border border-white/30 hover:border-white/50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-6 sm:px-8 py-3 sm:py-4 rounded-2xl font-semibold text-base sm:text-lg w-full sm:w-auto"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="bg-white/20 p-1.5 sm:p-2 rounded-xl group-hover:bg-white/30 transition-colors">
                    <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <span>Add User</span>
                </div>
                
                {/* Subtle glow effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-white/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col lg:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search users"
              className="pl-10 pr-4 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as FilterType)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="all">All Users</option>
            <option value="active">Active Users</option>
            <option value="inactive">Inactive Users</option>
            <option value="admin">Admin Users</option>
          </select>

          <select
            value={sortType}
            onChange={(e) => setSortType(e.target.value as SortType)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          >
            <option value="name">Sort by Name</option>
            <option value="role">Sort by Role</option>
            <option value="created">Sort by Created</option>
            <option value="login">Sort by Last Login</option>
          </select>

          <div className="flex items-center border border-gray-300 rounded-lg bg-white">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-blue-500'}`}
            >
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'text-gray-400 hover:text-blue-500'}`}
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Results Info */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-gray-600 font-medium">
          {searchTerm || filterType !== 'all' ? (
            <>Showing {filteredUsers.length} of {stats.total} users</>
          ) : (
            <>Showing all {stats.total} users</>
          )}
        </p>
      </div>

      {/* User Grid/List */}
      {filteredUsers.length === 0 ? (
        <Card className="p-12 text-center border-2 border-dashed border-gray-300">
          <div className="flex flex-col items-center gap-4">
            <div className="bg-gradient-to-br from-purple-100 to-purple-200 p-4 rounded-full">
              <Users className="h-8 w-8 text-purple-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 tracking-tight">
                {searchTerm ? 'No users found' : 'No users yet'}
              </h3>
              <p className="text-gray-600 mb-4 max-w-md">
                {searchTerm 
                  ? 'Try adjusting your search or filters to find what you\'re looking for.'
                  : 'Get started by adding your first user to the system.'
                }
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => {/* Handle add user */}}
                  className="group relative bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-8 py-4 rounded-xl font-semibold text-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-lg group-hover:bg-white/30 transition-colors">
                      <Plus className="h-5 w-5" />
                    </div>
                    <span>Add Your First User</span>
                  </div>
                  
                  {/* Subtle glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-purple-500 rounded-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 -z-10 blur-xl"></div>
                </Button>
              )}
            </div>
          </div>
        </Card>
      ) : (
        <div className={
          viewMode === 'grid' 
            ? 'grid gap-4 grid-cols-1 lg:grid-cols-2' 
            : 'space-y-3'
        }>
          {filteredUsers.map(user => 
            viewMode === 'grid' ? renderUserCard(user) : renderListItem(user)
          )}
        </div>
      )}

      <UserDetailModal
        userId={selectedUserId}
        open={isDetailModalOpen}
        onOpenChange={setIsDetailModalOpen}
        onUserUpdated={() => window.location.reload()}
      />
    </div>
  );
};

export default UserManagementPage;